// components/common/OrderProductCard.tsx
import { OrderStatus, Product, formatPrice, getStatusStyle, isBook, formatAuthorsDisplay } from '@bookstore/shared';
import { Truck, CheckCircle2, Clock, XCircle, AlertCircle, Info, Calendar, RotateCcw, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Placeholder from "@/assets/placeholder.webp";
import { getStatusIcon } from '@/utils/getStatusIcon';

interface OrderProductCardProps {
  product: Product;
  quantity: number;
  orderStatus: OrderStatus;
  deliveredOn?: string;
}

const OrderProductCard = ({
  product,
  quantity,
  orderStatus,
  deliveredOn,
}: OrderProductCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const productUrl = `/product/${product.seoUrl}`;

  // Choose status color & icon


  const status = getStatusStyle(orderStatus);

  return (
    <div className="group bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 flex gap-5 hover:shadow-md transition-all duration-300">
      {/* Image */}
      <Link
        to={productUrl}
        className="w-24 sm:w-28 h-32 sm:h-36 rounded-xl overflow-hidden flex-shrink-0 relative bg-gray-50"
      >
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 bg-[length:200%_100%] animate-shimmer" />
        )}
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.title}
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl text-gray-400">
            <img
              src={Placeholder}
              alt="Placeholder"
              onLoad={() => setImageLoaded(true)}
              className={`w-full h-full object-cover transition-all duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            />
          </div>
        )}
      </Link>

      {/* Details */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <Link
            to={productUrl}
            className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 hover:text-gray-700 transition-colors"
          >
            {product.title}
          </Link>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5" title={isBook(product) ? product.author : (product as any).brand || 'Stationery'}>
            {isBook(product)
              ? formatAuthorsDisplay(product.author, 2)
              : (product as any).brand || 'Stationery'}
          </p>
        </div>

        <div className="flex items-center justify-between mt-3">
          <div className="flex flex-col">
            <p className="text-sm text-gray-500">Qty: {quantity}</p>
            <p className="text-base font-bold text-gray-900">
              {formatPrice((product.discountedPrice || product.price) * quantity)}
            </p>
          </div>
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium  ${status.bg} ${status.color}`}
          >

            {/* convert icon string to icons */}
            {getStatusIcon(status.icon)}     
            {status.label}
          </div>
        </div>

        {/* Delivery info (optional) */}
        {orderStatus === 'delivered' && deliveredOn && (
          <p className="text-xs text-gray-500 mt-2">
            Delivered on <span className="font-medium">{deliveredOn}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default OrderProductCard;
